import type { z } from 'zod';
import { logger } from '@/shared/observability/logger';
import { captureException } from '@/shared/observability/sentry';

/**
 * Parse an API response with a Zod schema. Logs and re-throws on validation
 * failure so we surface drift between frontend and backend contracts.
 *
 * Returns the schema's *output* type (after defaults are applied), which is
 * what z.infer<> resolves to for callers.
 */
export function parseWithSchema<S extends z.ZodTypeAny>(
  schema: S,
  data: unknown,
  context: string,
): z.output<S> {
  const result = schema.safeParse(data);
  if (!result.success) {
    logger.error(
      {
        event: 'api.parse_error',
        context,
        issues: result.error.issues,
      },
      `Failed to parse response for ${context}`,
    );
    captureException(result.error, { context, data });
    throw result.error;
  }
  return result.data;
}
